import type { ComponentPropsWithRef, CSSProperties, ReactNode } from "react";
import { cn } from "../../lib/cn";

export interface SimpleGridProps extends ComponentPropsWithRef<"div"> {
  /** Nombre de colonnes (1 à 12) ou objet de breakpoints { base, sm, md, lg }. */
  cols?: number | { base?: number; sm?: number; md?: number; lg?: number; xl?: number };
  /** Largeur minimale des enfants pour la grille auto-fit réactive (ex: "280px"). */
  minChildWidth?: string;
  /** Espacement entre les cellules (0 à 12). Par défaut 4. */
  gap?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12;
  children?: ReactNode;
}

const GAP_CLASSES = {
  0: "gap-0",
  1: "gap-1",
  2: "gap-2",
  3: "gap-3",
  4: "gap-4",
  5: "gap-5",
  6: "gap-6",
  8: "gap-8",
  10: "gap-10",
  12: "gap-12",
} as const;

/**
 * Grille CSS SimpleGrid réactive SOTA.
 * Calcule automatiquement les colonnes ou adapte la grille réactive via `minChildWidth`.
 */
export function SimpleGrid({
  className,
  cols = 1,
  minChildWidth,
  gap = 4,
  style,
  children,
  ...props
}: SimpleGridProps) {
  const customStyle: CSSProperties = { ...style };

  if (minChildWidth) {
    customStyle.gridTemplateColumns = `repeat(auto-fit, minmax(${minChildWidth}, 1fr))`;
  }

  let colClasses = "";
  if (!minChildWidth) {
    if (typeof cols === "number") {
      colClasses = `grid-cols-${cols}`;
    } else {
      const { base = 1, sm, md, lg, xl } = cols;
      colClasses = cn(
        `grid-cols-${base}`,
        sm && `sm:grid-cols-${sm}`,
        md && `md:grid-cols-${md}`,
        lg && `lg:grid-cols-${lg}`,
        xl && `xl:grid-cols-${xl}`,
      );
    }
  }

  return (
    <div
      className={cn("grid", GAP_CLASSES[gap], colClasses, className)}
      style={customStyle}
      {...props}
    >
      {children}
    </div>
  );
}

export interface GridProps extends ComponentPropsWithRef<"div"> {
  cols?: number;
  gap?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12;
  children?: ReactNode;
}

export function Grid({ className, cols = 12, gap = 4, children, ...props }: GridProps) {
  return (
    <div className={cn("grid", `grid-cols-${cols}`, GAP_CLASSES[gap], className)} {...props}>
      {children}
    </div>
  );
}

export interface GridItemProps extends ComponentPropsWithRef<"div"> {
  span?: number;
  spanSm?: number;
  spanMd?: number;
  spanLg?: number;
  children?: ReactNode;
}

export function GridItem({
  className,
  span = 1,
  spanSm,
  spanMd,
  spanLg,
  children,
  ...props
}: GridItemProps) {
  return (
    <div
      className={cn(
        `col-span-${span}`,
        spanSm && `sm:col-span-${spanSm}`,
        spanMd && `md:col-span-${spanMd}`,
        spanLg && `lg:col-span-${spanLg}`,
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}
